import { useEffect, useState, type FormEvent } from 'react';
import { ApiError } from '../../api/client';
import { offers, requests, sourcing } from '../../api/endpoints';
import type {
    CommercialOffer,
    FulfillmentOption,
    OfferLine,
    PriceContext,
    RequestItem,
    SupplierQuote
} from '../../api/types';
import {
    Card, Column, DataTable, Empty, ErrorBox, Field, fmtDate, fmtMoney, JsonBlock, KV,
    shortId,
    StatusBadge
} from '../../components/ui';
import { useAsync } from '../../hooks';
import type { CardData } from '../RequestCardPage';

const CURRENCIES = ['RUB', 'USD', 'EUR', 'CNY'];

function OfferBlock({ offer, data, onChanged }: { offer: CommercialOffer; data: CardData; onChanged: () => void }) {
  const lines = useAsync<OfferLine[]>(() => offers.lines(offer.id), [offer.id]);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [preview, setPreview] = useState<unknown>(null);
  const [previewErr, setPreviewErr] = useState<unknown>(null);
  const [actionErr, setActionErr] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true); setActionErr(null);
    try { await fn(); onChanged(); lines.reload(); } catch (x) { setActionErr(x); } finally { setBusy(false); }
  };

  const loadPreview = async () => {
    setPreviewErr(null);
    try {
      setPreview(await offers.preview(offer.id));
    } catch (x) {
      setPreview(null);
      setPreviewErr(x instanceof ApiError && x.isNotFound
        ? new Error('Endpoint предпросмотра пока не реализован на сервере — используйте данные строк ниже.')
        : x);
    }
  };

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const lineCols: Column<OfferLine>[] = [
    {
      key: 'sel', title: '', width: '30px',
      render: (r) => (
        <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} />
      ),
    },
    { key: 'item', title: 'Позиция', render: (r) => data.items.find((i) => i.id === r.item)?.title ?? shortId(r.item) },
    { key: 'option', title: 'Вариант', render: (r) => shortId(r.option) },
    { key: 'sale_price', title: 'Цена продажи', render: (r) => fmtMoney(r.sale_price, r.currency) },
  ];

  const isDraft = (offer.status ?? 'draft') === 'draft';
  const isSent = offer.status === 'sent' || Boolean(offer.sent_at);

  return (
    <Card
      title={`Предложение v${offer.version ?? '?'} — ${offer.id.slice(0, 8)}`}
      actions={<StatusBadge value={offer.status} />}
    >
      <div className="kv-grid" style={{ marginBottom: 8 }}>
        <div className="kv"><span className="kv-key">Отправлено</span><span className="kv-value">{fmtDate(offer.sent_at)}</span></div>
        <div className="kv"><span className="kv-key">Принято</span><span className="kv-value">{fmtDate(offer.accepted_at)}</span></div>
        <div className="kv"><span className="kv-key">Отклонено</span><span className="kv-value">{fmtDate(offer.rejected_at)}</span></div>
        <div className="kv"><span className="kv-key">Revision</span><span className="kv-value">{offer.revision ?? '—'}</span></div>
      </div>

      <ErrorBox error={lines.error} onRetry={lines.reload} />
      <DataTable columns={lineCols} rows={lines.data ?? []} empty="Строк нет — добавьте первую" />

      <ErrorBox error={actionErr} />
      <div className="form-actions" style={{ marginTop: 12 }}>
        <button className="btn small" onClick={loadPreview} disabled={busy}>Предпросмотр</button>
        {isDraft && (
          <button className="btn small primary" disabled={busy}
                  onClick={() => { if (window.confirm('Подтвердить отправку предложения клиенту?')) void run(() => offers.markSent(offer.id, { channel: 'manual', revision: offer.revision })); }}>
            Отметить отправленным
          </button>
        )}
        {isSent && offer.status !== 'accepted' && (
          <button className="btn small primary" disabled={busy || selected.size === 0}
                  onClick={() => { if (window.confirm(`Зафиксировать согласование по выбранным строкам (${selected.size})?`)) void run(() => offers.accept(offer.id, { line_ids: [...selected], revision: offer.revision })); }}>
            Согласовать выбранные ({selected.size})
          </button>
        )}
        {isSent && offer.status !== 'accepted' && offer.status !== 'rejected' && (
          <button className="btn small danger" disabled={busy}
                  onClick={() => { const reason = window.prompt('Причина отклонения:', ''); if (reason !== null) void run(() => offers.reject(offer.id, { reason, revision: offer.revision })); }}>
            Отклонить
          </button>
        )}
      </div>

      {previewErr ? <ErrorBox error={previewErr} /> : null}
      {preview ? (
        <div className="section" style={{ marginTop: 12 }}>
          <h3>Предпросмотр предложения</h3>
          {typeof preview === 'string' ? <pre className="json-block">{preview}</pre> : <JsonBlock data={preview} />}
        </div>
      ) : null}
      {offer.sent_snapshot && Object.keys(offer.sent_snapshot).length > 0 && (
        <div className="section" style={{ marginTop: 12 }}>
          <h3>Снимок отправленного предложения</h3>
          <JsonBlock data={offer.sent_snapshot} />
        </div>
      )}
    </Card>
  );
}

/** Ценовой контекст одной строки КП: себестоимость, политика, прайс клиента, история.
 *  Загружается через GET price-preview; смена расчёта/цены обновляет только эту строку. */
function LinePriceContext({ itemId, quoteId, salePrice, currency }: {
  itemId: string; quoteId: string; salePrice: string; currency: string;
}) {
  // Дебаунс цены/валюты, чтобы не дёргать API на каждый keystroke.
  const [debounced, setDebounced] = useState({ salePrice, currency });
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced({ salePrice, currency }), 350);
    return () => window.clearTimeout(t);
  }, [salePrice, currency]);

  const ctx = useAsync<PriceContext>(
    () => requests.pricePreview(itemId, {
      quote_id: quoteId,
      sale_price: debounced.salePrice !== '' ? debounced.salePrice : undefined,
      currency: debounced.currency || undefined,
    }),
    [itemId, quoteId, debounced.salePrice, debounced.currency]);

  const c = ctx.data;
  const history = c?.history;
  const saleCurrency = debounced.currency || c?.cost_currency || '';
  const historyText = !history || !history.count
    ? 'нет данных'
    : [
        `последняя ${fmtMoney(history.last_price, saleCurrency)}`,
        `средняя ${fmtMoney(history.average_price, saleCurrency)}`,
        `сделок: ${history.count}`,
      ].join(' · ');

  return (
    <div className="section price-context" style={{ marginTop: 8 }} data-quote={quoteId}>
      <div className="field-hint" style={{ marginBottom: 4 }}>Ценовой контекст строки</div>
      <ErrorBox error={ctx.error} onRetry={ctx.reload} />
      {ctx.loading && !c ? <div className="muted">Загрузка ценового контекста…</div> : null}
      {c ? (
        <div className="kv-grid">
          <KV k="Себестоимость" v={fmtMoney(c.cost, c.cost_currency)} />
          <KV k="Минимум по политике" v={fmtMoney(c.minimum_price, c.policy_currency)} />
          <KV k="Рекомендованная цена" v={fmtMoney(c.recommended_price, c.policy_currency)} />
          <KV k="Индивидуальный прайс клиента"
              v={c.client_price != null ? fmtMoney(c.client_price, c.client_price_currency) : 'нет данных'} />
          <KV k="История продаж клиенту" v={historyText} />
          <KV k={c.margin_note || 'Разница до налогов'}
              v={c.pre_tax_difference != null ? fmtMoney(c.pre_tax_difference, saleCurrency) : '—'} />
          {/* === WT4 finance (§11): состав расчёта по налоговой модели политики === */}
          {c.margin ? (
            <>
              <KV k={c.margin.label ?? 'Маржа'}
                  v={c.margin.composition?.net_margin != null
                    ? fmtMoney(c.margin.composition.net_margin, c.margin.composition.currency ?? saleCurrency)
                    : (c.margin.reason ?? '—')} />
              {c.margin.composition?.vat_amount != null ? (
                <KV k={c.margin.composition.tax_label
                        ?? `НДС ${c.margin.composition.vat_rate ?? ''}%`}
                    v={fmtMoney(c.margin.composition.vat_amount, c.margin.composition.currency)} />
              ) : null}
              <KV k="Ценовая политика"
                  v={c.margin.policy_id
                    ? `${shortId(c.margin.policy_id)} · версия ${c.margin.policy_version ?? '?'}`
                    : 'нет'} />
              {c.margin.below_minimum ? (
                <KV k="Контроль минимума" v="цена ниже минимума" />
              ) : null}
              {c.margin.status === 'undefined' && c.margin.reason ? (
                <KV k="Причина неопределённости" v={c.margin.reason} />
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

interface DraftLineState {
  key: number;
  quoteId: string;
  price: string;
  currency: string;
}

let draftLineSeq = 0;
const newDraftLine = (currency = 'RUB'): DraftLineState => ({ key: ++draftLineSeq, quoteId: '', price: '', currency });

function CreateDraftForm({ data, onDone }: { data: CardData; onDone: () => void }) {
  const quotes = useAsync<SupplierQuote[]>(() => sourcing.quotes({ status: 'confirmed' }), []);
  const [lines, setLines] = useState<DraftLineState[]>(() => [newDraftLine()]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);

  // Только опубликованные/подтверждённые расчёты текущей заявки:
  // quote.option_id обязан ссылаться на вариант исполнения позиции этой заявки.
  const itemById = new Map<string, RequestItem>(data.items.map((i) => [i.id, i] as [string, RequestItem]));
  const optionById = new Map<string, FulfillmentOption>(
    data.options.filter((o) => itemById.has(o.item)).map((o) => [o.id, o] as [string, FulfillmentOption]));
  const visibleQuotes = (quotes.data ?? []).filter(
    (q) => q.option_id != null && optionById.has(q.option_id) && (q.published_at != null || q.status === 'confirmed'));
  const quoteById = new Map<string, SupplierQuote>(visibleQuotes.map((q) => [q.id, q] as [string, SupplierQuote]));

  const lineItemId = (line: DraftLineState): string | undefined => {
    const q = quoteById.get(line.quoteId);
    const opt = q?.option_id ? optionById.get(q.option_id) : undefined;
    return opt?.item;
  };
  const quoteItemTitle = (q: SupplierQuote): string => {
    const opt = q.option_id ? optionById.get(q.option_id) : undefined;
    return (opt && itemById.get(opt.item)?.title) || '';
  };
  const quoteOptionLabel = (q: SupplierQuote): string => {
    const opt = q.option_id ? optionById.get(q.option_id) : undefined;
    const label = opt ? [opt.country, opt.scheme, opt.series_or_batch].filter(Boolean).join(' · ') : '';
    return label || (opt ? shortId(opt.id) : '');
  };

  // Одна котировка не может быть выбрана дважды; разные котировки/варианты
  // одной позиции остаются доступны как альтернативы.
  const usedQuoteIds = new Set(lines.map((l) => l.quoteId).filter((x) => x !== ''));
  const incomplete = lines.some((l) => !l.quoteId || !l.price);
  // Независимые позиции суммируются только в одной валюте — все строки обязаны совпадать.
  const currencyMismatch = new Set(lines.map((l) => l.currency)).size > 1;

  const updateLine = (key: number, patch: Partial<DraftLineState>) =>
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const addLine = () => setLines((prev) => [...prev, newDraftLine(prev[0]?.currency ?? 'RUB')]);
  const removeLine = (key: number) =>
    setLines((prev) => (prev.length > 1 ? prev.filter((l) => l.key !== key) : prev));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (incomplete || currencyMismatch) return;
    setBusy(true); setErr(null);
    try {
      await offers.createDraft(data.request.id, data.request.revision, lines.map((l) => ({
        quote_id: l.quoteId,
        sale_price: l.price,
        currency: l.currency,
      })));
      setLines([newDraftLine()]);
      onDone();
    } catch (x) {
      // 400/409: введённые строки не сбрасываются — менеджер исправляет и повторяет.
      setErr(x);
    } finally { setBusy(false); }
  };

  // Итог для контекста: альтернативы одной позиции в сумму не входят.
  const linesByItem = new Map<string, DraftLineState[]>();
  for (const l of lines) {
    const itemId = lineItemId(l);
    if (!itemId) continue;
    linesByItem.set(itemId, [...(linesByItem.get(itemId) ?? []), l]);
  }
  const hasAlternatives = [...linesByItem.values()].some((ls) => ls.length > 1);
  // Суммируются только выбранные (первые по порядку) расчёты разных позиций,
  // и только внутри одной валюты — при смешанных валютах итог раздельный.
  const totalsByCurrency = new Map<string, number>();
  for (const ls of linesByItem.values()) {
    const first = ls[0];
    if (!first || first.price === '') continue;
    const amount = Number(first.price);
    if (Number.isNaN(amount)) continue;
    totalsByCurrency.set(first.currency, (totalsByCurrency.get(first.currency) ?? 0) + amount);
  }
  const singleCurrency = totalsByCurrency.size === 1 ? [...totalsByCurrency.keys()][0] : null;

  return (
    <form className="inline-form" onSubmit={submit}>
      <ErrorBox error={quotes.error} onRetry={quotes.reload} />
      <ErrorBox error={err} />
      {!quotes.loading && !quotes.error && visibleQuotes.length === 0 && (
        <Empty text="Нет опубликованных расчётов по позициям текущей заявки" />
      )}

      {lines.map((line, idx) => {
        const q = quoteById.get(line.quoteId);
        const itemId = lineItemId(line);
        const item = itemId ? itemById.get(itemId) : undefined;
        const isAlternative = itemId !== undefined
          && lines.slice(0, idx).some((l) => lineItemId(l) === itemId);
        const selectable = visibleQuotes.filter((x) => x.id === line.quoteId || !usedQuoteIds.has(x.id));
        return (
          <div key={line.key} className="section" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <h3 style={{ margin: 0 }}>
                Строка {idx + 1}{item ? ` — ${item.title}` : ''}{isAlternative ? ' (альтернатива)' : ''}
              </h3>
              {lines.length > 1 && (
                <button type="button" className="btn small danger" onClick={() => removeLine(line.key)}>
                  Удалить строку
                </button>
              )}
            </div>
            <div className="form-row" style={{ marginTop: 8 }}>
              <Field label="Опубликованный расчёт">
                <select value={line.quoteId} onChange={(e) => updateLine(line.key, { quoteId: e.target.value })} required>
                  <option value="">— выберите —</option>
                  {selectable.map((sq) => (
                    <option key={sq.id} value={sq.id}>
                      {sq.anonymous_code ?? sq.id.slice(0, 8)}
                      {quoteItemTitle(sq) ? ` · ${quoteItemTitle(sq)}` : ''} — {fmtMoney(sq.cost, sq.currency)}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Цена продажи">
                <input type="number" step="0.01" value={line.price}
                       onChange={(e) => updateLine(line.key, { price: e.target.value })} required />
              </Field>
              <Field label="Валюта">
                <select value={line.currency} onChange={(e) => updateLine(line.key, { currency: e.target.value })}>
                  {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
            </div>
            {q && (
              <div className="kv-grid" style={{ marginTop: 4 }}>
                <KV k="Код расчёта" v={q.anonymous_code ?? shortId(q.id)} />
                <KV k="Позиция / вариант" v={`${quoteItemTitle(q) || '—'}${quoteOptionLabel(q) ? ` · ${quoteOptionLabel(q)}` : ''}`} />
                <KV k="Стоимость расчёта" v={fmtMoney(q.cost, q.currency)} />
                <KV k="Срок" v={q.duration != null
                  ? `${q.duration} ${q.duration_unit === 'days' ? 'дн.' : (q.duration_unit ?? '')}${q.day_type ? ` (${q.day_type === 'calendar' ? 'календ. дни' : q.day_type === 'working' ? 'раб. дни' : q.day_type})` : ''}`
                  : '—'} />
                <KV k="Действует до" v={fmtDate(q.valid_until)} />
              </div>
            )}
            {q?.commercial_conditions && Object.keys(q.commercial_conditions).length > 0 && (
              <div style={{ marginTop: 4 }}>
                <div className="field-hint">Коммерческие условия расчёта</div>
                <JsonBlock data={q.commercial_conditions} />
              </div>
            )}
            {q && itemId && (
              <LinePriceContext
                itemId={itemId}
                quoteId={line.quoteId}
                salePrice={line.price}
                currency={line.currency}
              />
            )}
          </div>
        );
      })}

      {currencyMismatch && (
        <div className="alert alert-warn">
          <strong>Разные валюты.</strong>
          <span>Все строки предложения должны быть в одной валюте — независимые позиции суммируются только при совпадающей валюте.</span>
        </div>
      )}

      {linesByItem.size > 0 && !currencyMismatch && singleCurrency && (
        <div className="kv" style={{ marginTop: 4 }}>
          <span className="kv-key">Итого по независимым позициям</span>
          <span className="kv-value">
            {fmtMoney(totalsByCurrency.get(singleCurrency) ?? 0, singleCurrency)}
            {hasAlternatives && <span className="muted"> (альтернативы одной позиции в сумму не входят)</span>}
          </span>
        </div>
      )}

      {linesByItem.size > 0 && currencyMismatch && totalsByCurrency.size > 0 && (
        <div className="kv" style={{ marginTop: 4 }}>
          <span className="kv-key">Итоги по валютам (разные валюты — не суммируются)</span>
          <span className="kv-value">
            {[...totalsByCurrency.entries()].map(([cur, sum]) => fmtMoney(sum, cur)).join(' · ')}
            {hasAlternatives && <span className="muted"> (альтернативы одной позиции в сумму не входят)</span>}
          </span>
        </div>
      )}

      <div className="form-actions">
        <button type="button" className="btn" onClick={addLine}>+ Строка / альтернатива</button>
        <button className="btn primary" disabled={busy || incomplete || currencyMismatch || visibleQuotes.length === 0}>
          {busy ? 'Создание…' : `Создать черновик предложения (${lines.length} строк)`}
        </button>
      </div>
    </form>
  );
}

export function OffersTab({ data }: { data: CardData }) {
  const list = useAsync<CommercialOffer[]>(() => offers.list({ request: data.request.id }), [data.request.id]);
  const [showDraft, setShowDraft] = useState(false);

  return (
    <div>
      <div className="toolbar">
        <button className="btn primary" onClick={() => setShowDraft((v) => !v)}>
          {showDraft ? 'Скрыть форму' : 'Новое предложение (черновик)'}
        </button>
      </div>
      {showDraft && <CreateDraftForm data={data} onDone={() => { setShowDraft(false); list.reload(); }} />}
      <ErrorBox error={list.error} onRetry={list.reload} />
      {(list.data ?? []).length === 0 && !list.loading
        ? <Card><Empty text="Предложений нет" /></Card>
        : (list.data ?? []).map((o) => (
          <OfferBlock key={o.id} offer={o} data={data} onChanged={() => { list.reload(); data.reload(); }} />
        ))}
    </div>
  );
}
